import { useCallback, useMemo } from "react";
import { APP_LANGUAGE } from "../constants/intl.ts";

type NumberFormatterOptions = Pick<
  Intl.NumberFormatOptions,
  "style" | "minimumFractionDigits" | "maximumFractionDigits"
>;

/**
 * Generates a formatted number string based on the provided options.
 *
 * Example usage:
 *
 * ```ts
 * const formatNumber = useNumberFormatter({ style: 'currency', minimumFractionDigits: 2 });
 * const formatted = formatNumber(1234.5); // "$1,234.50" in en-US locale
 * ```
 */
export function useNumberFormatter({
  style = "decimal",
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
}: NumberFormatterOptions = {}) {
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(APP_LANGUAGE, {
        style,
        minimumFractionDigits,
        maximumFractionDigits,
      }),
    [style, minimumFractionDigits, maximumFractionDigits],
  );

  return useCallback((n: number) => formatter.format(n), [formatter]);
}
